import { HealingAiInputNormalization, HealingItem, Rectangle } from "@/core/types"
import { BrushStroke } from "@/core/utils/brushing"
import { imageDataToBase64 } from "@/core/utils/image-data"
import { vec2 } from "gl-matrix"
import { toInitialOrientation, rectToInitialOrientation } from "@/lib/ai/alignment/ai-image-align"
import { isAIDebugEnabled, isHealingDebugDownloadsEnabled } from "@/utils/measurement/feature-flag"

// ============================================================================
// CACHE ID UTILITIES
// ============================================================================

/**
 * Generate cache ID for healing render data
 * Format: ${healing.id}_v${variation}_a${alignment}_b${brushMask}
 * This is used by both the asset resolver and healing renderer to ensure consistent caching
 *
 * @param healing - Healing item to generate cache ID for
 * @returns Cache ID string that uniquely identifies this healing's render state
 */
export const getHealingRenderDataId = (healing: HealingItem): string => {
	const selectedVariation = healing.selectedVariation ?? 0
	const baseId = healing.aiVariations ? `${healing.id}___v${selectedVariation}` : healing.id

	if (!healing.aiVariations) {
		return baseId
	}

	const align = healing.applyAlignment ?? true
	const brush = healing.applyBrushMask ?? true
	return `${baseId}_a${align ? "1" : "0"}_b${brush ? "1" : "0"}`
}

// ============================================================================
// DIRECTION UTILITIES - no longer used
// ============================================================================

/**
 * Check if a point is roughly at the center of a rectangle
 * @param center - Point to check (in normalized coordinates)
 * @param rect - Rectangle to check against
 * @param threshold - How close to center counts as "center" (fraction of rect dimensions)
 * @returns true if the point is within threshold of rect center
 */
export const isCenterOfRect = (center: vec2, rect: Rectangle, threshold = 0.15): boolean => {
	const rectCenterX = rect.x + rect.width / 2
	const rectCenterY = rect.y + rect.height / 2

	// Calculate distance from rect center as fraction of rect dimensions
	const distX = Math.abs(center[0] - rectCenterX) / rect.width
	const distY = Math.abs(center[1] - rectCenterY) / rect.height

	// Point is "center" if both distances are within threshold
	return distX < threshold && distY < threshold
}

/**
 * Get direction string for a point relative to rectangle center, accounting for orientation
 * @param center - Point to get direction for (in unoriented normalized coordinates)
 * @param rect - Reference rectangle (in unoriented normalized coordinates)
 * @param orientation - Number of 90° clockwise rotations applied to the image for AI
 * @returns Direction string like "top-right", "bottom", "center-left", etc.
 */
export const getDirectionFromCenter = (
	center: vec2,
	rect: Rectangle,
	orientation: number,
): string => {
	// Transform center and rect to oriented space (what AI sees)
	const orientedCenter = toInitialOrientation(center, orientation)
	const orientedRect = rectToInitialOrientation(rect, orientation)

	// Calculate direction in oriented space
	const rectCenterX = orientedRect.x + orientedRect.width / 2
	const rectCenterY = orientedRect.y + orientedRect.height / 2

	// Calculate relative position (-1 to 1 in each axis)
	const relX = (orientedCenter[0] - rectCenterX) / (orientedRect.width / 2)
	const relY = (orientedCenter[1] - rectCenterY) / (orientedRect.height / 2)

	// Determine direction in oriented space
	const threshold = 0.33 // Threshold for "center" vs edge regions

	let vertical = ""
	let horizontal = ""

	if (relY < -threshold) vertical = "top"
	else if (relY > threshold) vertical = "bottom"

	if (relX < -threshold) horizontal = "left"
	else if (relX > threshold) horizontal = "right"

	// Combine to form compound direction
	let direction = ""
	if (vertical && horizontal) {
		direction = `${vertical}-${horizontal}` // e.g., "top-right"
	} else if (vertical) {
		direction = vertical // e.g., "top"
	} else if (horizontal) {
		direction = horizontal // e.g., "right"
	} else {
		direction = "center"
	}

	return direction
}

// ============================================================================
// TYPES
// ============================================================================

// Types
export interface CapturedRegion {
	base64: string
	width: number // Oriented dimensions (sent to AI)
	height: number // Oriented dimensions (sent to AI)
	unorientedWidth: number // Un-oriented dimensions (for scaling after inverse rotation)
	unorientedHeight: number // Un-oriented dimensions (for scaling after inverse rotation)
	unorientedImageData: ImageData // Un-oriented image data for efficient mask overlay processing
	// Pre-calculated crop information for efficient bitmap processing
	cropOffsetX: number // offset in the scaled unoriented capture
	cropOffsetY: number
	cropWidth: number // crop width in the scaled unoriented capture
	cropHeight: number
	// Scaling information for large image handling
	scaleFactor: number // How much the capture was downscaled (1.0 = no scaling)
	originalWidth: number // Original capture size before any scaling
	originalHeight: number
	// Orientation information for AI processing
	finalOrientation: number // Total orientation that was applied to AI input
	aiInputNormalization: HealingAiInputNormalization
}

// ============================================================================
// GLOBAL CANVAS POOL FOR PERFORMANCE
// ============================================================================

let globalCanvas: HTMLCanvasElement | null = null
let globalContext: CanvasRenderingContext2D | null = null
let globalTempCanvas: OffscreenCanvas | null = null
let globalTempContext: OffscreenCanvasRenderingContext2D | null = null

/**
 * Get or create a reusable canvas with specified dimensions
 * Reuses existing canvas if dimensions match, otherwise resizes
 */
export const getGlobalCanvas = (
	width: number,
	height: number,
): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } => {
	if (!globalCanvas || !globalContext) {
		globalCanvas = document.createElement("canvas")
		// Set willReadFrequently for better getImageData() performance
		globalContext = globalCanvas.getContext("2d", { willReadFrequently: true })
		if (!globalContext) {
			throw new Error("Failed to get canvas context")
		}
	}

	// Resize only if needed to avoid unnecessary operations
	if (globalCanvas.width !== width || globalCanvas.height !== height) {
		globalCanvas.width = width
		globalCanvas.height = height
	}

	// Clear canvas for reuse
	globalContext.clearRect(0, 0, width, height)

	return { canvas: globalCanvas, ctx: globalContext }
}

/**
 * Get or create a reusable OffscreenCanvas with specified dimensions
 */
const getGlobalTempCanvas = (
	width: number,
	height: number,
): { canvas: OffscreenCanvas; ctx: OffscreenCanvasRenderingContext2D } => {
	if (!globalTempCanvas || !globalTempContext) {
		globalTempCanvas = new OffscreenCanvas(width, height)
		// Set willReadFrequently for better getImageData() performance
		globalTempContext = globalTempCanvas.getContext("2d", { willReadFrequently: true })
		if (!globalTempContext) {
			throw new Error("Failed to get OffscreenCanvas context")
		}
	}

	// Resize only if needed
	if (globalTempCanvas.width !== width || globalTempCanvas.height !== height) {
		globalTempCanvas.width = width
		globalTempCanvas.height = height
	}

	// Clear canvas for reuse
	globalTempContext.clearRect(0, 0, width, height)

	return { canvas: globalTempCanvas, ctx: globalTempContext }
}

// ============================================================================
// ORIENTATION UTILITIES
// ============================================================================

/**
 * Apply clockwise rotation to ImageData based on orientation value
 * @param imageData - Source image data to rotate
 * @param orientation - Number of 90-degree clockwise rotations (0, 1, 2, 3)
 * @returns Rotated ImageData with potentially swapped dimensions
 */
export const applyOrientation = (imageData: ImageData, orientation: number): ImageData => {
	if (orientation === 0) return imageData

	// Handle dimension swapping for 90°/270° rotations
	const needsSwap = Math.abs(orientation) % 2 === 1
	const targetWidth = needsSwap ? imageData.height : imageData.width
	const targetHeight = needsSwap ? imageData.width : imageData.height

	const { ctx } = getGlobalCanvas(targetWidth, targetHeight)
	const { canvas: tempCanvas, ctx: tempCtx } = getGlobalTempCanvas(
		imageData.width,
		imageData.height,
	)

	// Save context state and apply rotation around center
	ctx.save()
	ctx.translate(targetWidth / 2, targetHeight / 2)
	ctx.rotate((orientation * Math.PI) / 2)
	ctx.translate(-imageData.width / 2, -imageData.height / 2)

	// Draw rotated image
	tempCtx.putImageData(imageData, 0, 0)
	ctx.drawImage(tempCanvas, 0, 0)

	const result = ctx.getImageData(0, 0, targetWidth, targetHeight)

	// Restore context state for reuse
	ctx.restore()

	return result
}

/**
 * Apply counter-clockwise rotation to reverse orientation
 * @param imageData - Source image data to reverse-rotate
 * @param orientation - Original orientation to reverse
 * @returns ImageData rotated back to original orientation
 */
export const applyInverseOrientation = (imageData: ImageData, orientation: number): ImageData => {
	// Apply negative rotation to reverse the orientation
	return applyOrientation(imageData, -orientation)
}

/**
 * Rotate bitmap directly without ImageData conversions for better performance
 * @param bitmap - Source bitmap to rotate
 * @param orientation - Number of 90-degree clockwise rotations (negative for counter-clockwise)
 * @returns Promise<ImageBitmap> - Rotated bitmap
 */
export const rotateBitmap = async (
	bitmap: ImageBitmap,
	orientation: number,
): Promise<ImageBitmap> => {
	if (orientation === 0) return bitmap

	// Handle dimension swapping for 90°/270° rotations
	const needsSwap = Math.abs(orientation) % 2 === 1
	const targetWidth = needsSwap ? bitmap.height : bitmap.width
	const targetHeight = needsSwap ? bitmap.width : bitmap.height

	const { canvas, ctx } = getGlobalCanvas(targetWidth, targetHeight)

	// Save context state and apply rotation around center
	ctx.save()
	ctx.translate(targetWidth / 2, targetHeight / 2)
	ctx.rotate((orientation * Math.PI) / 2)
	ctx.translate(-bitmap.width / 2, -bitmap.height / 2)

	// Draw rotated bitmap directly
	ctx.drawImage(bitmap, 0, 0)

	// Restore context state
	ctx.restore()

	// Convert directly to bitmap
	const blob = await new Promise<Blob | null>((resolve) => {
		canvas.toBlob(resolve)
	})
	if (!blob) {
		throw new Error("Failed to create blob from canvas")
	}

	return await createImageBitmap(blob)
}

export const convertAlignmentToUnoriented = (
	alignmentOriented: {
		dx: number
		dy: number
		confidence: number
		scaleX?: number
		scaleY?: number
	},
	orientation: number, // number of 90° clockwise rotations; can be negative
	unorientedWidth: number,
	unorientedHeight: number,
): {
	dx: number
	dy: number
	confidence: number
	scaleX?: number
	scaleY?: number
} => {
	// Normalize to [0,3]
	const steps = ((orientation % 4) + 4) % 4

	// Rotate normalized displacement by inverse orientation
	// Inverse of CW steps is CCW steps, which corresponds to these mappings
	let dxNorm = alignmentOriented.dx
	let dyNorm = alignmentOriented.dy
	if (steps === 1) {
		// 90°: (dx, dy) → (dy, -dx)
		;[dxNorm, dyNorm] = [dyNorm, -dxNorm]
	} else if (steps === 2) {
		// 180°: (dx, dy) → (-dx, -dy)
		;[dxNorm, dyNorm] = [-dxNorm, -dyNorm]
	} else if (steps === 3) {
		// 270°: (dx, dy) → (-dy, dx)
		;[dxNorm, dyNorm] = [-dyNorm, dxNorm]
	}

	// Scale normalized values to un-oriented bitmap pixel dimensions
	const dx = dxNorm * unorientedWidth
	const dy = dyNorm * unorientedHeight

	// Map per-axis scales with orientation (swap on odd steps 90/270)
	let scaleX = alignmentOriented.scaleX ?? 1
	let scaleY = alignmentOriented.scaleY ?? 1
	if (steps % 2 === 1) {
		;[scaleX, scaleY] = [scaleY, scaleX]
	}

	return {
		dx,
		dy,
		confidence: alignmentOriented.confidence,
		scaleX,
		scaleY,
	}
}

/**
 * Check if the destination rectangle is near the edges of the image.
 */
export function isNearEdge(destinationRect: Rectangle, EDGE_THRESHOLD: number = 0.001) {
	const nearLeftEdge = destinationRect.x < EDGE_THRESHOLD
	const nearRightEdge = destinationRect.x + destinationRect.width > 1 - EDGE_THRESHOLD
	const nearTopEdge = destinationRect.y < EDGE_THRESHOLD
	const nearBottomEdge = destinationRect.y + destinationRect.height > 1 - EDGE_THRESHOLD
	return { nearLeftEdge, nearRightEdge, nearTopEdge, nearBottomEdge }
}

/**
 * Clamp alignment offsets and scales to prevent referencing pixels outside image bounds
 * when destination rectangle is near edges
 */
export function clampAlignmentAtEdges(options: {
	offsetX: number // in unoriented ai-bitmap pixel units
	offsetY: number // in unoriented ai-bitmap pixel units
	scaleX: number
	scaleY: number
	destinationRect: Rectangle
	cropRect: Rectangle
	fullBitmapWidth: number
	fullBitmapHeight: number
}): { offsetX: number; offsetY: number; scaleX: number; scaleY: number } {
	const {
		offsetX,
		offsetY,
		scaleX,
		scaleY,
		destinationRect,
		cropRect,
		fullBitmapWidth,
		fullBitmapHeight,
	} = options

	// destinationRect is where healing will be placed in normalized [0,1] image coordinates
	// cropRect is the brushed region within the full capture in normalized [0,1] coordinates
	// We only need to clamp if destination is actually near an edge
	// (if in middle of image, black regions can be filled with existing image pixels)
	const { nearLeftEdge, nearRightEdge, nearTopEdge, nearBottomEdge } = isNearEdge(destinationRect)

	// If not near any edge, no need to clamp
	if (!nearLeftEdge && !nearRightEdge && !nearTopEdge && !nearBottomEdge) {
		return { offsetX, offsetY, scaleX, scaleY }
	}

	let clampedOffsetX = offsetX
	let clampedOffsetY = offsetY
	let clampedScaleX = scaleX
	let clampedScaleY = scaleY

	if (nearLeftEdge && nearRightEdge && clampedScaleX <= 1) {
		clampedScaleX = 1
		clampedOffsetX = 0
	} else {
		// Calculate crop dimensions inside the ai bitmap in pixels, because offsets are in this unit
		const cropAbsW = cropRect.width * fullBitmapWidth
		let scaleOffsetX = (cropAbsW * (1 - scaleX)) / 2

		// Total effective offset (direct offset + scale-induced offset)
		const effectiveOffsetXLeft = offsetX + scaleOffsetX
		if (nearLeftEdge && effectiveOffsetXLeft > 0) {
			if (clampedScaleX < 1) {
				// adjust scale to half of the original scale or ideal scale
				const idealScaleX = 1 + (offsetX * 2) / cropAbsW
				clampedScaleX = Math.min(idealScaleX, (clampedScaleX + 1) / 2)
				scaleOffsetX = (cropAbsW * (1 - clampedScaleX)) / 2
			}
			clampedOffsetX = -scaleOffsetX
		}

		const effectiveOffsetXRight = offsetX - scaleOffsetX
		if (nearRightEdge && effectiveOffsetXRight < 0) {
			if (clampedScaleX < 1) {
				const idealScaleX = 1 - (offsetX * 2) / cropAbsW
				clampedScaleX = Math.min(idealScaleX, (clampedScaleX + 1) / 2)
				scaleOffsetX = (cropAbsW * (1 - clampedScaleX)) / 2
			}
			clampedOffsetX = scaleOffsetX
		}
	}

	if (nearTopEdge && nearBottomEdge && clampedScaleY <= 1) {
		clampedScaleY = 1
		clampedOffsetY = 0
	} else {
		const cropAbsH = cropRect.height * fullBitmapHeight
		let scaleOffsetY = (cropAbsH * (1 - scaleY)) / 2

		const effectiveOffsetYTop = offsetY + scaleOffsetY
		if (nearTopEdge && effectiveOffsetYTop > 0) {
			if (clampedScaleY < 1) {
				const idealScaleY = 1 + (offsetY * 2) / cropAbsH
				clampedScaleY = Math.min(idealScaleY, (clampedScaleY + 1) / 2)
				scaleOffsetY = (cropAbsH * (1 - clampedScaleY)) / 2
			}
			clampedOffsetY = -scaleOffsetY
		}

		const effectiveOffsetYBottom = offsetY - scaleOffsetY
		if (nearBottomEdge && effectiveOffsetYBottom < 0) {
			if (clampedScaleY < 1) {
				const idealScaleY = 1 - (offsetY * 2) / cropAbsH
				clampedScaleY = Math.min(idealScaleY, (clampedScaleY + 1) / 2)
				scaleOffsetY = (cropAbsH * (1 - clampedScaleY)) / 2
			}
			clampedOffsetY = scaleOffsetY
		}
	}

	// Log if clamping occurred
	if (
		clampedOffsetX !== offsetX ||
		clampedOffsetY !== offsetY ||
		clampedScaleX !== scaleX ||
		clampedScaleY !== scaleY
	) {
		const edges: string[] = []
		if (nearLeftEdge) edges.push("L")
		if (nearRightEdge) edges.push("R")
		if (nearTopEdge) edges.push("T")
		if (nearBottomEdge) edges.push("B")

		console.log(
			`⚠️ Alignment clamped at ${edges.join("/")} edge(s):`,
			`dx: ${offsetX.toFixed(2)} → ${clampedOffsetX.toFixed(2)}px,`,
			`dy: ${offsetY.toFixed(2)} → ${clampedOffsetY.toFixed(2)}px,`,
			`sX: ${scaleX.toFixed(4)} → ${clampedScaleX.toFixed(4)},`,
			`sY: ${scaleY.toFixed(4)} → ${clampedScaleY.toFixed(4)}`,
		)
	}

	return {
		offsetX: clampedOffsetX,
		offsetY: clampedOffsetY,
		scaleX: clampedScaleX,
		scaleY: clampedScaleY,
	}
}

// ============================================================================
// CANVAS DRAWING UTILITIES
// ============================================================================

/**
 * Draw brush strokes on canvas with round caps and joins
 * @param ctx - Canvas 2D context to draw on
 * @param strokes - Array of brush strokes to render
 * @param paddedRect - Bounding rectangle for coordinate transformation
 * @param captureWidth - Target canvas width
 * @param captureHeight - Target canvas height
 * @param fillStyle - Fill color (unused in current stroke-only implementation)
 * @param strokeStyle - Stroke color
 */
export const drawStrokesOnCanvas = (
	ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
	strokes: BrushStroke[],
	paddedRect: Rectangle,
	captureWidth: number,
	captureHeight: number,
	fillStyle: string,
	strokeStyle: string,
) => {
	ctx.fillStyle = fillStyle
	ctx.strokeStyle = strokeStyle
	ctx.globalCompositeOperation = "source-over"

	for (const stroke of strokes) {
		const { points, size } = stroke

		const scaleX = captureWidth / paddedRect.width
		const scaleY = captureHeight / paddedRect.height
		const brushSizePixels = size * Math.min(scaleX, scaleY)

		if (points.length > 2) {
			// Draw multi-point stroke as path
			ctx.beginPath()
			for (let i = 0; i < points.length; i += 2) {
				const currentPoint = points[i]
				const nextPoint = points[i + 1]

				if (currentPoint !== undefined && nextPoint !== undefined) {
					const x = ((currentPoint - paddedRect.x) / paddedRect.width) * captureWidth
					const y = ((nextPoint - paddedRect.y) / paddedRect.height) * captureHeight

					if (i === 0) {
						ctx.moveTo(x, y)
					} else {
						ctx.lineTo(x, y)
					}
				}
			}
			ctx.lineWidth = brushSizePixels
			ctx.lineCap = "round"
			ctx.lineJoin = "round"
			ctx.stroke()
		} else if (points.length === 2 && points[0] !== undefined && points[1] !== undefined) {
			// Handle single-point stroke (2 coordinates: x, y) as a circle
			const x = ((points[0] - paddedRect.x) / paddedRect.width) * captureWidth
			const y = ((points[1] - paddedRect.y) / paddedRect.height) * captureHeight

			ctx.beginPath()
			ctx.arc(x, y, brushSizePixels / 2, 0, 2 * Math.PI)
			ctx.fillStyle = fillStyle
			ctx.fill()
		}
	}
}

// ============================================================================
// MASK GENERATION UTILITIES
// ============================================================================

/**
 * Generate a binary mask from brush strokes for AI models
 * @param strokes - Brush strokes to convert to mask
 * @param paddedRect - Bounding rectangle for coordinate transformation
 * @param captureWidth - Mask width in pixels
 * @param captureHeight - Mask height in pixels
 * @returns ImageData with white strokes on black background
 */
export const generateMaskBitmap = (
	strokes: BrushStroke[],
	paddedRect: Rectangle,
	captureWidth: number,
	captureHeight: number,
): ImageData => {
	const { ctx } = getGlobalCanvas(captureWidth, captureHeight)

	// Fill with black background (masked area will be white)
	ctx.fillStyle = "black"
	ctx.fillRect(0, 0, captureWidth, captureHeight)

	// Draw white strokes to create the mask
	drawStrokesOnCanvas(ctx, strokes, paddedRect, captureWidth, captureHeight, "white", "white")

	return ctx.getImageData(0, 0, captureWidth, captureHeight)
}

export const generateMaskImage = (
	strokes: BrushStroke[],
	paddedRect: Rectangle,
	capture: CapturedRegion,
): string => {
	const originalMaskImageData = generateMaskBitmap(
		strokes,
		paddedRect,
		capture.unorientedWidth,
		capture.unorientedHeight,
	)

	// Apply the same orientation to the mask as we applied to the image
	const orientedMaskImageData = applyOrientation(originalMaskImageData, capture.finalOrientation)
	const maskBase64 = maskImageDataToBase64(orientedMaskImageData)

	// DEBUG: Download mask for inspection
	downloadDebugImage(orientedMaskImageData, "flux-fill-pro-mask-oriented")

	return maskBase64
}

/**
 * Convert ImageData to base64 PNG for mask generation (masks need exact precision)
 * @param imageData - Binary mask image data
 * @returns Base64-encoded PNG string
 */
export const maskImageDataToBase64 = (imageData: ImageData): string => {
	// Masks must use PNG format for exact black/white precision
	// Binary masks should compress extremely well (mostly black background + white strokes)
	const result = imageDataToBase64(imageData, "png")
	console.log(
		`🎭 Binary mask generated: ${imageData.width}x${imageData.height} → ~${Math.round(
			(result.length * 0.75) / 1024,
		)}KB (PNG compresses binary data very efficiently)`,
	)
	return result
}

// ============================================================================
// MASK OVERLAY UTILITIES
// ============================================================================

/**
 * Apply white mask overlay to image for erasing mode visualization
 * @param capture - Captured region with unoriented image data
 * @param strokes - Brush strokes to overlay
 * @param paddedRect - Bounding rectangle for coordinate transformation
 * @returns Base64-encoded JPEG image with white mask overlay, properly oriented
 */
export const applyWhiteMaskOverlay = (
	capture: CapturedRegion,
	strokes: BrushStroke[],
	paddedRect: Rectangle,
): string => {
	const { ctx } = getGlobalCanvas(capture.unorientedWidth, capture.unorientedHeight)

	// Draw original unoriented image directly from ImageData
	ctx.putImageData(capture.unorientedImageData, 0, 0)

	// Draw white mask overlay using shared drawing utility (in unoriented space)
	drawStrokesOnCanvas(
		ctx,
		strokes,
		paddedRect,
		capture.unorientedWidth,
		capture.unorientedHeight,
		"white",
		"white",
	)

	// Get composite image data and apply orientation
	const compositeImageData = ctx.getImageData(
		0,
		0,
		capture.unorientedWidth,
		capture.unorientedHeight,
	)
	const orientedComposite = applyOrientation(compositeImageData, capture.finalOrientation)

	// debug download
	downloadDebugImage(orientedComposite, "white-mask")

	// Return as JPEG 0.99 - nearly lossless but smaller than PNG
	return imageDataToBase64(orientedComposite, "jpeg", 0.99)
}

/**
 * Apply red mask overlay to image for mask visualization
 * @param capture - Captured region with unoriented image data
 * @param strokes - Brush strokes to overlay
 * @param paddedRect - Bounding rectangle for coordinate transformation
 * @returns Base64-encoded JPEG image with red mask overlay, properly oriented
 */
export const applyRedMaskOverlay = (
	capture: CapturedRegion,
	strokes: BrushStroke[],
	paddedRect: Rectangle,
): string => {
	const { ctx } = getGlobalCanvas(capture.unorientedWidth, capture.unorientedHeight)

	// Draw original unoriented image directly from ImageData
	ctx.putImageData(capture.unorientedImageData, 0, 0)

	// Draw red transparent mask overlay - simple stroke-only approach (in unoriented space)
	const redColor = "rgba(255, 0, 0, 0.3)"
	drawStrokesOnCanvas(
		ctx,
		strokes,
		paddedRect,
		capture.unorientedWidth,
		capture.unorientedHeight,
		redColor,
		redColor,
	)

	// Get composite image data and apply orientation
	const compositeImageData = ctx.getImageData(
		0,
		0,
		capture.unorientedWidth,
		capture.unorientedHeight,
	)
	const orientedComposite = applyOrientation(compositeImageData, capture.finalOrientation)

	// debug download
	downloadDebugImage(orientedComposite, "red-mask")

	// Return as JPEG 0.99 - nearly lossless but smaller than PNG
	return imageDataToBase64(orientedComposite, "jpeg", 0.99)
}

/**
 * Apply a red outline that traces the brushed silhouette for erasing mode.
 *
 * Uses the same "stroke then hollow" trick as `HealingEdge.drawOutlines`:
 *   1. Stroke/fill every brush path at full brush width in solid red on a temp canvas.
 *   2. Re-stroke/fill the same paths with `destination-out` at a slightly smaller width
 *      to erase the interior, leaving only a ring that hugs the true brushed shape.
 *   3. Composite the outlined ring onto the captured image.
 *
 * Compared to an axis-aligned ellipse, this gives the AI model a red contour that
 * matches the actual brush shape (straight swipes stay thin, blobs stay round), so it
 * doesn't over-cover empty space. Compared to a translucent filled mask, the interior
 * pixels stay undistorted, which tends to help image-edit models preserve context.
 *
 * @param capture - Captured region with unoriented image data
 * @param strokes - Brush strokes whose silhouette outline should be rendered
 * @param paddedRect - Bounding rectangle for coordinate transformation
 * @returns Base64-encoded JPEG image with the red outline overlay, properly oriented
 */
export const applyRedOutlineOverlay = (
	capture: CapturedRegion,
	strokes: BrushStroke[],
	paddedRect: Rectangle,
	options: { thicknessRatio?: number; minThicknessPx?: number } = {},
): string => {
	const { thicknessRatio = 0.003, minThicknessPx = 4 } = options
	const W = capture.unorientedWidth
	const H = capture.unorientedHeight

	const { ctx } = getGlobalCanvas(W, H)
	ctx.putImageData(capture.unorientedImageData, 0, 0)

	// Build the outline on a temp canvas so destination-out doesn't erase the photo.
	const { canvas: tempCanvas, ctx: tempCtx } = getGlobalTempCanvas(W, H)

	// Same capture-pixel scaling drawStrokesOnCanvas uses for brush size.
	const scaleX = W / paddedRect.width
	const scaleY = H / paddedRect.height
	const minScale = Math.min(scaleX, scaleY)

	// Outline thickness in capture pixels — scales with capture size for visibility.
	// Tweak thicknessRatio / minThicknessPx via options to make it thinner or thicker.
	const outlineThicknessPx = Math.max(minThicknessPx, Math.round(minScale * thicknessRatio))

	// Draw every stroke as a filled/stroked path at (brushSizePx + sizeDeltaPx).
	// Uses the exact same point-mapping and single-point-arc fallback as drawStrokesOnCanvas
	// so the silhouette matches what the user painted.
	const renderStrokes = (sizeDeltaPx: number) => {
		for (const stroke of strokes) {
			const { points, size } = stroke
			const brushSizePx = size * minScale + sizeDeltaPx
			if (brushSizePx <= 0) continue
			const finalSize = Math.max(1, brushSizePx)

			if (points.length > 2) {
				tempCtx.beginPath()
				for (let i = 0; i < points.length; i += 2) {
					const px = points[i]
					const py = points[i + 1]
					if (px === undefined || py === undefined) continue
					const x = ((px - paddedRect.x) / paddedRect.width) * W
					const y = ((py - paddedRect.y) / paddedRect.height) * H
					if (i === 0) tempCtx.moveTo(x, y)
					else tempCtx.lineTo(x, y)
				}
				tempCtx.lineWidth = finalSize
				tempCtx.lineCap = "round"
				tempCtx.lineJoin = "round"
				tempCtx.stroke()
			} else if (points.length === 2 && points[0] !== undefined && points[1] !== undefined) {
				const x = ((points[0] - paddedRect.x) / paddedRect.width) * W
				const y = ((points[1] - paddedRect.y) / paddedRect.height) * H
				tempCtx.beginPath()
				tempCtx.arc(x, y, finalSize / 2, 0, 2 * Math.PI)
				tempCtx.fill()
			}
		}
	}

	tempCtx.globalCompositeOperation = "source-over"
	tempCtx.fillStyle = "red"
	tempCtx.strokeStyle = "red"
	renderStrokes(0)

	// Constrain the hollowing pass to an inset rectangle so any silhouette that
	// reaches a patch edge keeps a solid red border there — this closes the outline
	// at the image boundary instead of leaving it open where the brush was clipped.
	const insetW = Math.max(0, W - 2 * outlineThicknessPx)
	const insetH = Math.max(0, H - 2 * outlineThicknessPx)
	const canClip = insetW > 0 && insetH > 0
	if (canClip) {
		tempCtx.save()
		tempCtx.beginPath()
		tempCtx.rect(outlineThicknessPx, outlineThicknessPx, insetW, insetH)
		tempCtx.clip()
	}

	tempCtx.globalCompositeOperation = "destination-out"
	tempCtx.fillStyle = "rgba(0,0,0,1)"
	tempCtx.strokeStyle = "rgba(0,0,0,1)"
	renderStrokes(-2 * outlineThicknessPx)

	if (canClip) {
		tempCtx.restore()
	}
	tempCtx.globalCompositeOperation = "source-over"

	ctx.drawImage(tempCanvas, 0, 0)

	const compositeImageData = ctx.getImageData(0, 0, W, H)
	const orientedComposite = applyOrientation(compositeImageData, capture.finalOrientation)

	downloadDebugImage(orientedComposite, "red-outline")

	return imageDataToBase64(orientedComposite, "jpeg", 0.99)
}

/**
 * Helper: Apply erosion to shrink a mask uniformly by sampling circle perimeter
 * Much faster than full kernel - only samples ~2πr points instead of r² points
 * @param imageData - Mask image data to erode (white on black)
 * @param radius - Erosion radius in pixels (e.g., 8 shrinks by ~8 pixels)
 */
const erodeMask = (imageData: ImageData, radius: number): void => {
	const { data, width, height } = imageData
	const tempData = new Uint8ClampedArray(width * height)

	// Copy current red channel values
	for (let i = 0; i < width * height; i++) {
		tempData[i] = data[i * 4] ?? 0 // Red channel
	}

	// Pre-compute circle sample points (only once)
	// Sample points evenly around the circle perimeter
	const numSamples = Math.min(20, Math.ceil(2 * Math.PI * radius)) // ~2πr samples or 20 points max
	const angleStep = (2 * Math.PI) / numSamples
	const circleSamples: Array<{ dx: number; dy: number }> = []

	for (let i = 0; i < numSamples; i++) {
		const angle = i * angleStep
		circleSamples.push({
			dx: Math.round(Math.cos(angle) * radius),
			dy: Math.round(Math.sin(angle) * radius),
		})
	}

	// Apply erosion by checking circle perimeter
	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			const idx = y * width + x

			// Find minimum value on the circle perimeter
			let minVal = 255

			// Check each sample point on the circle
			for (const sample of circleSamples) {
				const checkX = x + sample.dx
				const checkY = y + sample.dy

				// Skip out-of-bounds
				if (checkX < 0 || checkX >= width || checkY < 0 || checkY >= height) {
					continue
				}

				const neighborIdx = checkY * width + checkX
				const neighborVal = tempData[neighborIdx] ?? 0

				if (neighborVal < minVal) {
					minVal = neighborVal
					// Early exit optimization: if we find black, no need to continue
					if (minVal === 0) break
				}
			}

			// Set all channels to the eroded value
			const pixelIdx = idx * 4
			data[pixelIdx] = minVal // R
			data[pixelIdx + 1] = 0 // G
			data[pixelIdx + 2] = 0 // B
			data[pixelIdx + 3] = minVal // A
		}
	}
}

/**
 * Apply red mask overlay with uniformly shrunk mask (highly optimized)
 *
 * Performance optimizations:
 * 1. Downsamples mask to ~500px for faster erosion operations (~10× speedup)
 * 2. Uses circular perimeter sampling for erosion (~3-4× faster than box kernel)
 * 3. GPU-accelerated compositing via canvas operations (no CPU pixel loop)
 * 4. Direct colorization and upscaling (no temporary overlay canvas)
 *
 * Total performance: ~15ms for typical healing regions
 *
 * @param capture - Captured region data
 * @param strokes - Brush strokes to visualize
 * @param paddedRect - Bounding rectangle for coordinate transformation
 * @param shrinkAmount - Erosion radius in pixels (default: 15, shrinks by ~15 pixels in 500x canvas)
 *
 * @returns Base64 encoded JPEG image with red overlay
 */
export const applyShrinkedRedMaskOverlay = (
	capture: CapturedRegion,
	strokes: BrushStroke[],
	paddedRect: Rectangle,
	shrinkAmount: number = 15,
): string => {
	const { ctx } = getGlobalCanvas(capture.unorientedWidth, capture.unorientedHeight)

	// Draw original unoriented image directly from ImageData
	ctx.putImageData(capture.unorientedImageData, 0, 0)

	// OPTIMIZATION: Use smaller canvas for mask operations
	// Calculate downscale factor - target ~500px on the larger dimension
	const maxDim = Math.max(capture.unorientedWidth, capture.unorientedHeight)
	const targetSize = 500
	const scaleFactor = maxDim > targetSize ? targetSize / maxDim : 1
	const maskWidth = Math.round(capture.unorientedWidth * scaleFactor)
	const maskHeight = Math.round(capture.unorientedHeight * scaleFactor)

	console.log(`Mask canvas: ${maskWidth}×${maskHeight} (scale: ${scaleFactor.toFixed(2)}x)`)

	// Create mask on a smaller canvas
	const { ctx: maskCtx, canvas: maskCanvas } = getGlobalTempCanvas(maskWidth, maskHeight)
	maskCtx.fillStyle = "black"
	maskCtx.fillRect(0, 0, maskWidth, maskHeight)

	// Draw strokes directly in RED (erodeMask will handle alpha automatically)
	const redColor = "red"
	drawStrokesOnCanvas(maskCtx, strokes, paddedRect, maskWidth, maskHeight, redColor, redColor)

	// Get mask as ImageData
	const maskImageData = maskCtx.getImageData(0, 0, maskWidth, maskHeight)

	// Apply erosion to shrink the mask uniformly (on smaller canvas)
	// This will set R and Alpha to the same eroded value automatically
	const scaleX = maskWidth / paddedRect.width
	const scaleY = maskHeight / paddedRect.height
	const size = strokes.reduce((min, stroke) => Math.min(min, stroke.size), 1)
	const brushSizePixels = size * Math.min(scaleX, scaleY)
	shrinkAmount = Math.min(shrinkAmount, Math.round(brushSizePixels * 0.25))

	if (shrinkAmount >= 1) {
		console.time(`Eroding mask with radius ${shrinkAmount}`)
		erodeMask(maskImageData, shrinkAmount)
		console.timeEnd(`Eroding mask with radius ${shrinkAmount}`)
	}

	// Put back the eroded red mask (alpha already correct from erodeMask!)
	maskCtx.putImageData(maskImageData, 0, 0)

	// Draw the red mask directly to main canvas (upscaled) with alpha blending
	ctx.globalCompositeOperation = "source-over"
	ctx.globalAlpha = 0.2
	ctx.drawImage(maskCanvas, 0, 0, capture.unorientedWidth, capture.unorientedHeight)
	ctx.globalAlpha = 1.0

	// Get final composite and apply orientation
	const finalComposite = ctx.getImageData(0, 0, capture.unorientedWidth, capture.unorientedHeight)
	const orientedComposite = applyOrientation(finalComposite, capture.finalOrientation)

	// debug download
	downloadDebugImage(orientedComposite, "red-mask-shrunk")

	// Return as JPEG 0.99 - nearly lossless but smaller than PNG
	return imageDataToBase64(orientedComposite, "jpeg", 0.99)
}

export const bitmapToBlob = async (
	bitmap: ImageBitmap,
	format = "image/jpeg",
	quality = 0.92,
): Promise<Blob> => {
	const { canvas, ctx } = getGlobalCanvas(bitmap.width, bitmap.height)
	ctx.drawImage(bitmap, 0, 0)

	return new Promise<Blob>((resolve, reject) => {
		canvas.toBlob(
			(blob) => (blob ? resolve(blob) : reject(new Error("Conversion failed"))),
			format,
			quality,
		)
	})
}

export const texturePixelsToImageData = (pixels: Uint8Array, width: number, height: number) => {
	// No flipping - gl.readPixels already returns pixels in correct orientation
	return new ImageData(new Uint8ClampedArray(pixels), width, height)
}

export const base64ToBlob = (base64: string, mimeType: string) => {
	const byteCharacters = atob(base64)
	const byteNumbers = new Array(byteCharacters.length)
	for (let i = 0; i < byteCharacters.length; i++) {
		byteNumbers[i] = byteCharacters.charCodeAt(i)
	}
	const byteArray = new Uint8Array(byteNumbers)
	return new Blob([byteArray], { type: mimeType })
}

// const canvasToBlob = (canvas: HTMLCanvasElement, format = "image/jpeg", quality = 0.92) => {
// 	return new Promise<Blob>((resolve, reject) => {
// 		canvas.toBlob(
// 			(blob) => {
// 				if (blob) {
// 					resolve(blob)
// 				} else {
// 					reject(new Error("Unable to convert canvas to blob"))
// 				}
// 			},
// 			format,
// 			quality,
// 		)
// 	})
// }

export const blobToBase64 = (blob: Blob) => {
	return new Promise<string>((resolve, reject) => {
		const reader = new FileReader()
		reader.onloadend = () => {
			const result = reader.result
			if (typeof result === "string") {
				resolve(result.split(",")[1] ?? "")
			} else {
				reject(new Error("Failed to convert blob to base64"))
			}
		}
		reader.onerror = () => reject(new Error("Failed to read blob"))
		reader.readAsDataURL(blob)
	})
}

// ============================================================================
// DEBUG UTILITIES
// ============================================================================

/**
 * Download ImageData as PNG for debugging purposes
 * @param imageData - Image data to download
 * @param filename - Base filename (timestamp will be appended)
 */
export const downloadDebugImage = (
	imageData: ImageData,
	filename: string,
	forceDownload: boolean = false,
) => {
	if ((!isAIDebugEnabled() || !isHealingDebugDownloadsEnabled()) && !forceDownload) return

	const { canvas, ctx } = getGlobalCanvas(imageData.width, imageData.height)
	ctx.putImageData(imageData, 0, 0)
	canvas.toBlob((blob) => {
		if (blob) {
			const url = URL.createObjectURL(blob)
			const link = document.createElement("a")
			link.href = url
			link.download = `${filename}-${Date.now()}.png`
			link.style.display = "none"
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			URL.revokeObjectURL(url)
		}
	})
}

/**
 * Download blob as file for debugging purposes
 * @param blob - Blob to download
 * @param filename - Base filename (timestamp will be appended)
 */
export const downloadDebugBlob = (blob: Blob, filename: string) => {
	if (!isAIDebugEnabled() || !isHealingDebugDownloadsEnabled()) return

	const url = URL.createObjectURL(blob)
	const link = document.createElement("a")
	link.href = url
	link.download = `${filename}-${Date.now()}.png`
	link.style.display = "none"
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	URL.revokeObjectURL(url)
}
